import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  KeyIcon as Key,
  CircleNotchIcon as Spinner,
  WarningCircleIcon as WarningCircle,
} from "@phosphor-icons/react";
import { formatApiErrorDetail, useAuth } from "@/lib/auth";
import { resetPassword } from "@/lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (!token) {
      setError("Reset token is missing. Please use the link from your email.");
      return;
    }
    setSubmitting(true);
    try {
      await resetPassword(token, password);
      // Backend logs us in on success; sync the AuthContext.
      await refresh();
      navigate("/workspaces", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6 bg-[var(--ml-bg)]"
      data-testid="reset-password-page"
    >
      <form
        onSubmit={onSubmit}
        className="ml-card w-full max-w-md p-8 space-y-5"
        data-testid="reset-password-form"
      >
        <div>
          <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] flex items-center gap-1.5 mb-1">
            <Key size={11} weight="bold" />
            Choose a new password
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Reset your password
          </h1>
        </div>

        <label className="block">
          <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mb-1">
            New password
          </div>
          <input
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={8}
            data-testid="reset-password-new"
            className="w-full px-3 py-2 border border-[var(--ml-border)] focus:outline-none focus:border-[var(--ml-primary)] text-sm"
          />
          <div className="ml-mono text-[10px] text-[var(--ml-text-secondary)] mt-1">Minimum 8 characters.</div>
        </label>

        <label className="block">
          <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mb-1">
            Confirm new password
          </div>
          <input
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            minLength={8}
            data-testid="reset-password-confirm"
            className="w-full px-3 py-2 border border-[var(--ml-border)] focus:outline-none focus:border-[var(--ml-primary)] text-sm"
          />
        </label>

        {error && (
          <div
            className="flex items-start gap-2 text-sm text-[#a2191f] bg-[#fff1f1] border border-[#ffd7d9] px-3 py-2"
            data-testid="reset-password-error"
            role="alert"
          >
            <WarningCircle size={14} weight="bold" className="mt-0.5 shrink-0" />
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting || !password || !confirm}
          data-testid="reset-password-submit"
          className="w-full px-4 py-2 bg-[var(--ml-primary)] text-white font-semibold hover:bg-[var(--ml-primary-hover)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
        >
          {submitting ? <Spinner size={16} className="animate-spin" /> : null}
          {submitting ? "Updating…" : "Update password"}
        </button>

        <div className="text-sm text-[var(--ml-text-secondary)] text-center">
          <Link to="/login" className="text-[var(--ml-primary)] hover:underline font-semibold" data-testid="reset-back-to-login">
            Back to sign in
          </Link>
        </div>
      </form>
    </div>
  );
}
